import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE } from '../../../_helpers/aggrid.locale.de';
import { AgGridThemeService } from '../../../_services/ag-grid-theme.service';
import { ProjectService } from '../../../_services';
import { Store } from '../../../_models/store.interface';

/**
 * Wiederverwendbarer Filial-Picker als Modal mit filterbarem AG-Grid —
 * für Projekte mit sehr vielen Filialen. Datenbasis sind die
 * Projekt-Filialen (gleiche Quelle wie die Ticket-Maske); alternativ kann
 * eine externe Liste über `stores` hereingegeben werden.
 */
@Component({
  selector: 'app-store-picker',
  standalone: true,
  imports: [AgGridAngular],
  templateUrl: './store-picker.component.html',
})
export class StorePickerComponent implements OnInit {
  /** Projekt, dessen Filialen geladen werden (wenn keine externe Liste gesetzt ist) */
  @Input() projectId: number | null = null;

  /** Optionale externe Filial-Liste — wenn gesetzt, wird nicht selbst geladen */
  @Input() stores: Store[] | null = null;

  /** Emittiert die ausgewählte Filiale */
  @Output() storeSelected = new EventEmitter<Store>();

  /** Emittiert beim Schließen ohne Auswahl */
  @Output() closed = new EventEmitter<void>();

  protected hawkTheme = inject(AgGridThemeService).getTheme();
  protected readonly locale = AG_GRID_LOCALE_DE;
  private projectService = inject(ProjectService);
  private gridApi?: GridApi;

  protected rowData = signal<Store[]>([]);
  protected isLoading = signal<boolean>(false);

  protected colDefs: ColDef[] = [
    { field: 'nr', headerName: 'Nr.', width: 120 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    {
      headerName: 'Adresse',
      flex: 1,
      minWidth: 240,
      valueGetter: params => {
        const s = params.data as Store;
        const street = [s.address_street, s.address_street_no].filter(Boolean).join(' ');
        const city = [s.address_zip, s.address_city].filter(Boolean).join(' ');
        return [street, city].filter(Boolean).join(', ');
      },
    },
  ];

  protected defaultColDef: ColDef = { filter: true };

  ngOnInit(): void {
    if (this.stores) {
      this.rowData.set(this.stores);
      return;
    }
    if (this.projectId == null) return;

    this.isLoading.set(true);
    this.projectService.getStores(this.projectId).subscribe({
      next: data => {
        this.rowData.set(data);
        this.isLoading.set(false);
      },
      error: err => {
        console.error('Fehler beim Laden der Projekt-Filialen:', err);
        this.isLoading.set(false);
      },
    });
  }

  protected onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  protected quickSearchChanged(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.gridApi?.setGridOption('quickFilterText', value);
  }

  protected onRowClicked(event: RowClickedEvent): void {
    this.storeSelected.emit(event.data as Store);
  }

  protected close(): void {
    this.closed.emit();
  }
}
